import { ArtifactOpenButton } from "./ArtifactOpenButton";
import {
  useDocumentEditor,
  isEditableDocument,
} from "../document-editor/DocumentEditorContext";
import type { ReactNode } from "react";
import { Download, FileText, LoaderCircle, PackageOpen } from "lucide-react";
import type { Artifact } from "@lumen/shared";
import { t } from "../../i18n";
import { artifactDownloadUrl, safeArtifactUrl } from "./artifact-utils";

function sizeLabel(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.ceil(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function thumbnailFor(artifact: Artifact) {
  const url = safeArtifactUrl(artifact.url);
  return artifact.mimeType.startsWith("image/") &&
    artifact.mimeType !== "image/svg+xml"
    ? url
    : "";
}

/**
 * Compact artifact listing shared by the chat development panel and the
 * workspace editor. Upload actions differ per surface, so callers inject them.
 */
export function ConversationArtifactsList({
  artifacts,
  loading,
  error,
  onReload,
  renderActions,
  emptyMessage,
}: {
  artifacts: Artifact[] | null;
  loading: boolean;
  error: string;
  onReload?: () => void;
  renderActions?: (artifact: Artifact) => ReactNode;
  emptyMessage?: string;
}) {
  const editor = useDocumentEditor();
  if (loading && !artifacts)
    return (
      <div className="flex items-center gap-2 px-3 py-4 text-xs text-stone-400">
        <LoaderCircle size={14} className="animate-spin" />
        {t("Loading artifacts…")}
      </div>
    );
  if (error)
    return (
      <div className="m-2 rounded-lg bg-red-50 p-2 text-[11px] text-red-700 dark:bg-red-950/30 dark:text-red-300">
        {error}
      </div>
    );
  if (!artifacts?.length)
    return emptyMessage ? (
      <div className="px-3 py-6 text-center text-xs text-stone-500 dark:text-stone-400">
        <PackageOpen size={18} className="mx-auto mb-2 block" />
        {emptyMessage}
      </div>
    ) : null;
  return (
    <div className="flex flex-col gap-0.5 px-2 py-1">
      {artifacts.map((artifact) => {
        const url = safeArtifactUrl(artifact.url);
        const download = artifactDownloadUrl(
          artifact.url,
          artifact.downloadUrl,
        );
        const thumbnail = thumbnailFor(artifact);
        return (
          <div
            key={artifact.id}
            className="group flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-stone-100 dark:hover:bg-white/5"
          >
            {thumbnail ? (
              <img
                src={thumbnail}
                alt=""
                className="size-9 shrink-0 rounded-md object-cover"
              />
            ) : (
              <div className="grid size-9 shrink-0 place-items-center rounded-md bg-white text-lumen-700 shadow-sm dark:bg-white/10 dark:text-lumen-300">
                <FileText size={15} />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div
                className="truncate text-xs font-medium text-stone-700 dark:text-stone-200"
                title={artifact.name}
              >
                {artifact.name}
              </div>
              <div className="truncate text-[11px] text-stone-400 dark:text-stone-500">
                {artifact.mimeType} · {sizeLabel(artifact.sizeBytes)}
              </div>
            </div>
            {editor?.enabled && isEditableDocument(artifact.name) && (
              <button
                type="button"
                className="button min-h-10 px-2 text-xs"
                onClick={() => editor.open(artifact.id)}
                aria-label={`Edit ${artifact.name}`}
              >
                Edit
              </button>
            )}
            {renderActions?.(artifact)}
            <ArtifactOpenButton
              id={artifact.id}
              name={artifact.name}
              url={url}
            />
            <a
              href={
                artifactDownloadUrl(artifact.url, artifact.downloadUrl) ||
                undefined
              }
              className="rounded-lg p-1.5 text-stone-400 hover:bg-white hover:text-lumen-700 dark:hover:bg-white/10 dark:hover:text-lumen-300"
              title={t("Download artifact")}
            >
              <Download size={14} />
            </a>
          </div>
        );
      })}
    </div>
  );
}
